import { useEffect, useMemo, useState } from 'react'
import { Box, Button, TextField, Typography } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { icons } from '../../../../utils/icons'
import { Types } from '../../../../utils/enums'
import { httpFetch } from '../../../../utils/crud'
import { actionPlansServices } from '../../../../service/actionPlans'
import Loader from '../../../../components/loader'
import SimpleModal from '../../../../components/modal/simpleModal'
import SnackSimple from '../../../../components/snackbars/simple'
import { useSolveActionPlan } from './hooks/useSolveActionPlan'
import EvidenceUploader from './components/evidenceUploader'
import { useStyles } from './styles'
import { SolverEvidence } from './types'
import { buildAnswerActionPlanPayload } from './utils'

const SolveActionPlan = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const styles = useStyles()

  const numericId = id ? parseInt(id, 10) : undefined
  const { plan, evidences, isLoading, error } = useSolveActionPlan(numericId)
  const [localEvidences, setLocalEvidences] = useState<SolverEvidence[]>(evidences)
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [solutionComment, setSolutionComment] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    type: 'error' as Types,
  })

  useEffect(() => {
    setLocalEvidences(evidences)
  }, [evidences])

  const handleBack = () => {
    navigate('/dashboard/action-plan/created')
  }

  const handleEvidencesChange = (newEvidences: SolverEvidence[]) => {
    setLocalEvidences(newEvidences)
  }

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }))
  }

  const handleOpenConfirmModal = () => {
    setSubmitError('')
    setSolutionComment('')
    setIsConfirmModalOpen(true)
  }

  const handleCloseConfirmModal = () => {
    setIsConfirmModalOpen(false)
    setSolutionComment('')
    setSubmitError('')
  }

  const handleConfirmFinalize = async () => {
    const trimmedComment = solutionComment.trim()
    if (!trimmedComment) return

    setIsSubmitting(true)
    setSubmitError('')

    const payload = buildAnswerActionPlanPayload(plan?.id, trimmedComment, localEvidences)
    if (!payload) {
      setIsSubmitting(false)
      setSubmitError(t('actionPlan.errorLoadingPlanInfo'))
      return
    }

    try {
      const response = await actionPlansServices(httpFetch).answerActionPlanImplemented(payload)

      if (response?.errors) {
        throw new Error(t('actionPlan.solveActionPlan.snackbar.solveError'))
      }

      setIsConfirmModalOpen(false)
      setSolutionComment('')
      setSnackbar({
        open: true,
        message: t('actionPlan.solveActionPlan.snackbar.solveSuccess'),
        type: Types.SUCCESS,
      })
      navigate(`/dashboard/action-plan/created/${plan?.id}`)
    } catch (err) {
      const message = err instanceof Error ? err.message : t('actionPlan.solveActionPlan.snackbar.solveError')
      setIsConfirmModalOpen(false)
      setSubmitError(message)
      setSnackbar({
        open: true,
        message,
        type: Types.ERROR,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const hasUploadingFiles = useMemo(
    () => localEvidences.some((evidence) => evidence.files.some((file) => file.isUploading)),
    [localEvidences]
  )

  const hasErrorFiles = useMemo(
    () => localEvidences.some((evidence) => evidence.files.some((file) => file.error)),
    [localEvidences]
  )

  const requiredEvidencesMissing = useMemo(
    () =>
      localEvidences.some((evidence) => {
        if (!evidence.required || evidence.maxAmount <= 0) return false
        const currentCount =
          evidence.id === 'comment' ? evidence.comments.length : evidence.files.length
        return currentCount < evidence.maxAmount
      }),
    [localEvidences]
  )

  const isFinalizeDisabled =
    hasUploadingFiles || hasErrorFiles || requiredEvidencesMissing

  const isConfirmDisabled = !solutionComment.trim() || isSubmitting

  if (isLoading) {
    return (
      <Box sx={styles.container}>
        <Loader sx={{ height: 'calc(100% - 100px)' }} />
      </Box>
    )
  }

  if (error || !plan) {
    return (
      <Box sx={styles.container}>
        <Box sx={styles.header}>
          <Box sx={styles.backLink} onClick={handleBack}>
            {icons('arrow-left-fill')}
            <Typography>{t('actionPlan.solveActionPlan.backToList')}</Typography>
          </Box>
          <Typography sx={styles.title}>{t('actionPlan.solveActionPlan.title')}</Typography>
        </Box>
        <Box sx={styles.emptyState}>
          <Typography sx={styles.emptyStateText}>
            {t('actionPlan.errorLoadingPlanInfo')}
          </Typography>
          <Button variant='contained' color='primary' onClick={handleBack}>
            {t('general.returnToList')}
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <Box sx={styles.backLink} onClick={handleBack}>
          {icons('arrow-left-fill')}
          <Typography>{t('actionPlan.solveActionPlan.backToList')}</Typography>
        </Box>
        <Typography sx={styles.title}>{t('actionPlan.solveActionPlan.title')}</Typography>
      </Box>

      <Box sx={styles.solveActionPlanContainer}>
        <EvidenceUploader
          planId={plan.id}
          evidences={evidences}
          onChange={handleEvidencesChange}
        />
      </Box>

      <Box sx={styles.footer}>
        <Button
          variant='contained'
          color='primary'
          onClick={handleOpenConfirmModal}
          disabled={isFinalizeDisabled}
          sx={styles.finalizeButton}
        >
          {t('actionPlan.solveActionPlan.finalize')}
        </Button>
      </Box>

      <SimpleModal
        open={isConfirmModalOpen}
        close={handleCloseConfirmModal}
        back={handleCloseConfirmModal}
        next={handleConfirmFinalize}
        title={t('actionPlan.solveActionPlan.confirmFinalize.title')}
        description={t('actionPlan.solveActionPlan.confirmFinalize.description')}
        cancelText={t('actionPlan.solveActionPlan.confirmFinalize.cancel')}
        buttonText={t('actionPlan.solveActionPlan.confirmFinalize.confirm')}
        loadingButton
        isLoading={isSubmitting}
        disableButton={isConfirmDisabled}
        removeClose
        sx={{ width: '550px', borderRadius: '10px' }}
        containerBox={{
          sx: {
            width: '100% !important',
            margin: '0 !important',
            padding: '24px !important',
            '& .MuiTypography-root': {
              width: '100%',
              textAlign: 'center',
              fontSize: '18px',
            },
            '& .description': { textAlign: 'left', paddingTop: '10px', fontSize: '16px' },
          },
        }}
      >
        <TextField
          fullWidth
          multiline
          minRows={4}
          maxRows={6}
          placeholder={t('actionPlan.solveActionPlan.confirmFinalize.commentPlaceholder')}
          value={solutionComment}
          onChange={(event) => setSolutionComment(event.target.value)}
          disabled={isSubmitting}
          sx={{ borderRadius: '0px' }}
          variant='outlined'
        />
      </SimpleModal>

      <SnackSimple
        open={snackbar.open}
        close={handleCloseSnackbar}
        message={snackbar.message}
        type={snackbar.type}
      />
    </Box>
  )
}

export default SolveActionPlan